import React from "react";
import { Link } from "react-router-dom";
import {
  Check,
  ArrowUpRight,
  Printer,
  Briefcase,
  ScanLine,
  PackageCheck,
  Home,
  Anchor,
  Building2,
  HeartHandshake,
} from "lucide-react";
import { PageHero, GoldButton, GhostGoldButton, SectionEyebrow, FadedBackdrop } from "@/components/ui-bits";
import { IMG } from "@/constants/images";

const TIERS = [
  {
    name: "General Mobile Notary",
    tag: "Most Common",
    price: "$ Stamp Fee",
    priceSuffix: "+ travel & convenience",
    description:
      "Statutory notarial acts at your home, office, hospital, or care facility. Pricing is the state-regulated stamp fee per notarized signature, plus a transparent travel/convenience fee based on distance and time of day.",
    features: [
      "Florida statutory stamp fee per notarized signature",
      "Variable travel fee based on distance",
      "After-hours, weekend, and rush surcharges disclosed up front",
      "Witnesses available (when scheduled in advance)",
      "Document audit before signing — at no extra charge",
    ],
    testid: "pricing-tier-mobile",
  },
  {
    name: "Remote Online Notarization",
    tag: "Worldwide",
    price: "Flat Fee",
    priceSuffix: "per digital seal / session",
    description:
      "Per-session flat fee for Remote Online Notarization performed under our Florida commission. Pricing includes secure identity verification, encrypted video session, digital seal, and audit-trail recording.",
    features: [
      "Per-seal flat fee (statutory maximum applied)",
      "ID verification (KBA + credential analysis)",
      "Encrypted, recorded video session",
      "Tamper-evident digital seal & signature",
      "Worldwide signer eligibility (where lawful)",
    ],
    testid: "pricing-tier-ron",
  },
];

const PREMIUM_INCLUDED = [
  {
    icon: Printer,
    title: "Dual-Tray Laser Printing & Prep",
    body: "Simultaneous printing of legal and letter-size disclosures, deeds, and closing packets to maintain pristine document presentation.",
  },
  {
    icon: Briefcase,
    title: "Mobile / White-Glove Courier Service",
    body: "On-location execution at professional offices, private residences, local marinas, or bedside care facilities.",
  },
  {
    icon: ScanLine,
    title: "Same-Day Secure Scan-Backs",
    body: "High-resolution digital delivery of fully executed files to your team immediately following the signing.",
  },
  {
    icon: PackageCheck,
    title: "Tracked Drop-Off with Confirmation",
    body: "Immediate dispatch to FedEx/UPS or local courier drop-off, with real-time tracking numbers provided directly to your operations desk.",
  },
];

const PREMIUM_COVERAGE = [
  {
    icon: Home,
    title: "Real Estate Closings",
    body: "Smooth execution of Refinance, Purchase, HELOC, Seller, and complex Reverse Mortgage packages.",
  },
  {
    icon: Anchor,
    title: "High-Value Assets & Maritime",
    body: "Specialized closings for Yacht/Boat brokerages, Coast Guard documentation, and premium vehicle title transfers.",
  },
  {
    icon: Building2,
    title: "Corporate & Government Contracting",
    body: "Secure handling of International Entity Formations, Joint Venture Agreements, and sensitive corporate document portfolios.",
  },
  {
    icon: HeartHandshake,
    title: "Estate & Family Care Transition",
    body: "Empathetic, meticulous processing of comprehensive Wills, Living Trusts, and Long-Term Care onboarding packets.",
  },
];

const PREMIUM_PRICING_TIERS = [
  {
    title: "Standard Portfolio Packages",
    body: "Flat-rate pricing tailored by package size, e-signing/hybrid complexity, and specific drop-off requirements.",
  },
  {
    title: "E-Sign & Hybrid Options",
    body: "Seamless digital integration available for modern, paperless transaction workflows.",
  },
  {
    title: "Emergency / After-Hours Availability",
    body: "Expedited priority scheduling available for high-intent, time-sensitive requirements.",
  },
];

export default function PricingPage() {
  return (
    <>
      <PageHero
        eyebrow="Transparent Pricing"
        title="Clear rates. No surprise fees. State-compliant — every time."
        lead="Our pricing is structured around three service categories. Statutory notary stamp fees comply with Florida state-regulated maximums. Travel, convenience, and after-hours fees are quoted up front and disclosed in your appointment confirmation."
        backdrop={IMG.notaryStamp}
        backdropOpacity={0.5}
        bright
      />

      <section className="relative overflow-hidden py-24 sm:py-32 border-b border-white/10">
        <FadedBackdrop src={IMG.realEstateSigning} opacity={0.2} />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-6 lg:gap-8 max-w-5xl mx-auto">
            {TIERS.map((t) => (
              <div
                key={t.name}
                data-testid={t.testid}
                className="relative flex flex-col bg-[#1A2B45] border border-white/10 p-8 sm:p-10 hover:border-[#D4AF37]/40 transition-colors"
              >
                <div className="eyebrow mb-3">{t.tag}</div>
                <h3 className="font-serif-display text-3xl text-white mb-4">
                  {t.name}
                </h3>
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="font-serif-display text-4xl text-[#D4AF37]">
                    {t.price}
                  </span>
                </div>
                <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-6">
                  {t.priceSuffix}
                </div>
                <p className="text-slate-300 text-sm leading-relaxed mb-8">
                  {t.description}
                </p>
                <ul className="space-y-3 mb-10 flex-1">
                  {t.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <Check className="w-4 h-4 text-[#D4AF37] mt-1 shrink-0" />
                      <span className="text-slate-200 text-sm">{f}</span>
                    </li>
                  ))}
                </ul>
                <GhostGoldButton
                  as={Link}
                  to="/contact"
                  data-testid={`${t.testid}-cta`}
                >
                  Request a Quote <ArrowUpRight className="w-4 h-4" />
                </GhostGoldButton>
              </div>
            ))}
          </div>

          {/* PREMIUM B2B HORIZONTAL CARD */}
          <div
            data-testid="pricing-tier-premium"
            className="relative mt-10 lg:mt-12 bg-[#1A2B45] border border-[#D4AF37] p-8 sm:p-12"
          >
            <div className="absolute -top-3 left-8 bg-[#D4AF37] text-[#142339] text-[10px] font-bold uppercase tracking-[0.22em] px-3 py-1">
              Featured · Premium
            </div>

            <div className="grid lg:grid-cols-12 gap-10 mb-12">
              <div className="lg:col-span-7">
                <div className="eyebrow mb-3">Flagship Package</div>
                <h3 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight leading-tight">
                  Premium B2B & Executive Signing Package
                </h3>
                <p className="mt-5 text-[#D4AF37] text-sm uppercase tracking-[0.22em] font-semibold">
                  For Title & Escrow · Marine Brokerages · Corporate Legal Teams · Family Estates
                </p>
                <p className="mt-6 text-slate-300 leading-relaxed">
                  A flat-rate, comprehensive execution package tailored for
                  complex, multi-page legal and financial document portfolios.
                  This all-inclusive service covers up to two signers and
                  ensures flawless completion of high-stakes contracts on your
                  exact timeline.
                </p>
              </div>
              <div className="lg:col-span-5 flex flex-col justify-between gap-6">
                <div className="border border-white/10 bg-[#142339]/60 p-6">
                  <div className="eyebrow mb-2">Pricing Format</div>
                  <div className="font-serif-display text-3xl text-[#D4AF37] leading-tight">
                    Flat Rate · Quoted Per Engagement
                  </div>
                  <p className="text-xs uppercase tracking-[0.2em] text-slate-400 mt-2">
                    All-inclusive · Up to 2 signers · Scope-based tiers
                  </p>
                </div>
                <GoldButton
                  as={Link}
                  to="/contact"
                  data-testid="pricing-tier-premium-cta"
                >
                  Request a Premium Quote <ArrowUpRight className="w-4 h-4" />
                </GoldButton>
              </div>
            </div>

            {/* What's Included */}
            <div className="border-t border-white/10 pt-10">
              <div className="eyebrow mb-5">What's Included in the Flat Rate</div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {PREMIUM_INCLUDED.map((it, i) => (
                  <div
                    key={i}
                    data-testid={`premium-included-${i}`}
                    className="border border-white/10 bg-[#142339]/40 p-5"
                  >
                    <div className="w-10 h-10 border border-[#D4AF37]/50 flex items-center justify-center text-[#D4AF37] mb-4">
                      <it.icon className="w-5 h-5" />
                    </div>
                    <div className="font-serif-display text-lg text-white mb-2 leading-tight">
                      {it.title}
                    </div>
                    <p className="text-slate-300 text-xs leading-relaxed">
                      {it.body}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Core Framework Coverage */}
            <div className="border-t border-white/10 pt-10 mt-10">
              <div className="eyebrow mb-5">Core Framework Coverage</div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {PREMIUM_COVERAGE.map((it, i) => (
                  <div
                    key={i}
                    data-testid={`premium-coverage-${i}`}
                    className="border border-white/10 bg-[#142339]/40 p-5"
                  >
                    <div className="w-10 h-10 border border-[#D4AF37]/50 flex items-center justify-center text-[#D4AF37] mb-4">
                      <it.icon className="w-5 h-5" />
                    </div>
                    <div className="font-serif-display text-lg text-white mb-2 leading-tight">
                      {it.title}
                    </div>
                    <p className="text-slate-300 text-xs leading-relaxed">
                      {it.body}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Dynamic Pricing Tiers */}
            <div className="border-t border-white/10 pt-10 mt-10">
              <div className="eyebrow mb-5">Pricing Matrix · Dynamic Tiers</div>
              <div className="grid sm:grid-cols-3 gap-5">
                {PREMIUM_PRICING_TIERS.map((it, i) => (
                  <div
                    key={i}
                    data-testid={`premium-pricing-${i}`}
                    className="border-l-2 border-[#D4AF37] pl-5"
                  >
                    <div className="font-serif-display text-lg text-white mb-2 leading-tight">
                      {it.title}
                    </div>
                    <p className="text-slate-400 text-xs leading-relaxed">
                      {it.body}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimers */}
      <section className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <SectionEyebrow className="mb-4">Disclaimers</SectionEyebrow>
          <h2 className="font-serif-display text-4xl text-white tracking-tight leading-tight max-w-3xl">
            Compliance with state-regulated notary fee maximums.
          </h2>
          <div
            data-testid="pricing-disclaimers"
            className="mt-10 border border-white/10 bg-[#1A2B45] p-8 sm:p-10 space-y-5 text-slate-300 text-sm leading-relaxed"
          >
            <p>
              <span className="text-white font-semibold">
                Statutory Notary Fees:
              </span>{" "}
              All notarial act fees comply with the maximum statutory fees
              established by the State of Florida for traditional in-person
              notarizations and for Remote Online Notarization (RON). The
              statutory fee applies per notarial act / per seal.
            </p>
            <p>
              <span className="text-white font-semibold">
                Travel & Convenience Fees:
              </span>{" "}
              Travel fees, convenience fees, and after-hours surcharges are
              separate from statutory notarial act fees. These are quoted up
              front based on distance, time of day, and complexity of the
              appointment, and are disclosed in writing in the appointment
              confirmation.
            </p>
            <p>
              <span className="text-white font-semibold">
                Premium B2B & Executive Signing Package:
              </span>{" "}
              Flat-fee pricing for the Premium B2B & Executive Signing Package
              reflects the bundled service of executing a complete legal or
              financial document portfolio — including pre-signing audit,
              dual-tray printing, on-location signing, same-day scan-backs,
              and tracked drop-off — and is not a statutory notarial fee.
            </p>
            <p>
              <span className="text-white font-semibold">No Legal Advice:</span>{" "}
              A Notary Public is not an attorney and is prohibited from
              providing legal advice, drafting legal documents, or interpreting
              the contents of any document. Notary fees are for notarial
              services only.
            </p>
            <p className="text-slate-500 text-xs">
              Prices and pricing structures shown on this page are placeholders
              pending final publication. Actual quotes are provided in writing
              prior to the appointment.
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
